export function resolveScrollOffsetToRestoreViewportAnchor({
  scrollOffset,
  anchorTop,
  currentAnchorTop,
}: {
  readonly scrollOffset: number;
  readonly anchorTop: number;
  readonly currentAnchorTop: number;
}): number | null {
  if (
    !Number.isFinite(scrollOffset) ||
    !Number.isFinite(anchorTop) ||
    !Number.isFinite(currentAnchorTop)
  ) {
    return null;
  }

  const delta = currentAnchorTop - anchorTop;
  return Math.abs(delta) < 0.5 ? null : Math.max(0, scrollOffset + delta);
}

export function navigationIntentLeftTimelineLiveEdge(input: {
  readonly isAtEnd: boolean;
  readonly navigationIntentPending: boolean;
}): boolean {
  return input.navigationIntentPending && !input.isAtEnd;
}

export interface TimelineNavigationIntentController {
  readonly beginPointerGesture: () => void;
  readonly deferClear: () => void;
  readonly isPending: () => boolean;
  readonly markUntilScrollFrame: () => void;
  readonly clearNow: () => void;
}

export function createTimelineNavigationIntentController(input: {
  readonly requestFrame: (callback: FrameRequestCallback) => number;
  readonly cancelFrame: (frameId: number) => void;
}): TimelineNavigationIntentController {
  let pending = false;
  let generation = 0;
  let clearFrame: number | null = null;

  const cancelScheduledClear = () => {
    if (clearFrame !== null) {
      input.cancelFrame(clearFrame);
      clearFrame = null;
    }
  };
  const clearNow = () => {
    generation += 1;
    pending = false;
    cancelScheduledClear();
  };
  const deferClear = () => {
    if (!pending) {
      return;
    }
    generation += 1;
    const scheduledGeneration = generation;
    cancelScheduledClear();
    clearFrame = input.requestFrame(() => {
      if (scheduledGeneration !== generation) {
        return;
      }
      // LegendList coalesces onScroll in its own RAF. Keep the intent alive
      // through that FIFO callback and clear only on the following frame.
      clearFrame = input.requestFrame(() => {
        if (scheduledGeneration !== generation) {
          return;
        }
        clearFrame = null;
        pending = false;
      });
    });
  };

  return {
    beginPointerGesture: () => {
      clearNow();
      pending = true;
    },
    deferClear,
    isPending: () => pending,
    markUntilScrollFrame: () => {
      pending = true;
      deferClear();
    },
    clearNow,
  };
}

export function bindTimelineNavigationIntentListeners(input: {
  readonly scrollNode: EventTarget;
  readonly windowTarget: EventTarget;
  readonly controller: TimelineNavigationIntentController;
}): () => void {
  const markNavigationIntentUntilScrollFrame = () => {
    input.controller.markUntilScrollFrame();
  };
  const handlePointerDown = () => {
    input.controller.beginPointerGesture();
  };
  const handlePointerEnd = () => {
    input.controller.deferClear();
  };
  const handleKeyboardNavigation = (event: Event) => {
    const keyboardEvent = event as KeyboardEvent;
    const targetElement = keyboardEvent.target instanceof Element ? keyboardEvent.target : null;
    if (
      keyboardIntentLeavesTimelineLiveFollow({
        key: keyboardEvent.key,
        targetConsumesNavigationKey: keyboardTargetConsumesTimelineNavigation(targetElement),
      })
    ) {
      markNavigationIntentUntilScrollFrame();
    }
  };

  input.scrollNode.addEventListener("wheel", markNavigationIntentUntilScrollFrame, {
    passive: true,
  });
  input.scrollNode.addEventListener("touchmove", markNavigationIntentUntilScrollFrame, {
    passive: true,
  });
  input.scrollNode.addEventListener("pointerdown", handlePointerDown, { passive: true });
  input.windowTarget.addEventListener("pointerup", handlePointerEnd, { passive: true });
  input.windowTarget.addEventListener("pointercancel", handlePointerEnd, { passive: true });
  input.scrollNode.addEventListener("keydown", handleKeyboardNavigation);

  return () => {
    input.controller.clearNow();
    input.scrollNode.removeEventListener("wheel", markNavigationIntentUntilScrollFrame);
    input.scrollNode.removeEventListener("touchmove", markNavigationIntentUntilScrollFrame);
    input.scrollNode.removeEventListener("pointerdown", handlePointerDown);
    input.windowTarget.removeEventListener("pointerup", handlePointerEnd);
    input.windowTarget.removeEventListener("pointercancel", handlePointerEnd);
    input.scrollNode.removeEventListener("keydown", handleKeyboardNavigation);
  };
}

export function resolveTimelineLiveFollowAfterEndState(input: {
  readonly liveFollowEnabled: boolean;
  readonly endState: { readonly isAtEnd: boolean; readonly isNearEnd: boolean };
}): boolean {
  return input.endState.isAtEnd || input.liveFollowEnabled;
}

const TIMELINE_SCROLL_KEYS = new Set([
  "PageUp",
  "PageDown",
  "Home",
  "End",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  " ",
]);

export const TIMELINE_KEYBOARD_INTERACTIVE_SELECTOR =
  'input, textarea, select, button, a[href], [contenteditable]:not([contenteditable="false"]), [role="textbox"]';

export function keyboardTargetConsumesTimelineNavigation(
  target: Pick<Element, "closest"> | null,
): boolean {
  return target !== null && target.closest(TIMELINE_KEYBOARD_INTERACTIVE_SELECTOR) !== null;
}

export function keyboardIntentLeavesTimelineLiveFollow(input: {
  readonly key: string;
  readonly targetConsumesNavigationKey: boolean;
}): boolean {
  return !input.targetConsumesNavigationKey && TIMELINE_SCROLL_KEYS.has(input.key);
}
